import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import {
  AlertCircleIcon,
  CheckIcon,
  DateIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
  UserIcon,
  ZapIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { getOrderDisplayFromTo } from '@hero/core';
import {
  isNowSlot,
  selectApproximateStops,
  selectIsReadyToPublish,
  selectSessionStats,
  selectTotalVehicles,
  usePublishOrderStore,
  type PublishSessionEntry,
} from '@hero/hooks-shared';
import { Card } from '../../../../app/Card/Card';
import { Button } from '../../../../ui/Button/Button';
import { OrderMapPreview } from './OrderMapPreview';
import { PriceSummaryCard } from './PriceSummaryCard';

export interface PublishSidePanelProps {
  /** Disabled while the publish mutation is in flight. */
  isPublishing: boolean;
  onPublish: () => void;
  /**
   * Called when the carrier picks the "Done" link in the side panel.
   * Rendered only when provided so embedders can opt out of the exit.
   */
  onDone?: () => void;
  /** Opens the load-details drawer. When present, the Load summary row becomes clickable. */
  onEditLoad?: () => void;
  /** Opens the pickup-date modal. When present, the Pickup summary row becomes clickable. */
  onEditPickup?: () => void;
}

/**
 * Sticky right-hand panel. Renders the route map preview on top, an
 * order summary card below, and the primary "Publish order" CTA at
 * the bottom. All data flows from the publish-order store so the
 * panel reacts to every form edit on the left immediately.
 */
export function PublishSidePanel({
  isPublishing,
  onPublish,
  onDone,
  onEditLoad,
  onEditPickup,
}: PublishSidePanelProps) {
  const { t, i18n } = useTranslation(['m-order', 'm-vehicle', 'c-main', 'c-dates']);

  const customer = usePublishOrderStore(s => s.customer);
  const isInternalShipment = usePublishOrderStore(s => s.isInternalShipment);
  // Mirrors `<PriceSummaryCard />`'s own gate. Reading it here lets
  // the footer divider drop with the card so we don't render a
  // standalone hairline when the card has nothing to say yet.
  const hasAnyDestination = usePublishOrderStore(s =>
    s.destinations.some(d => d && d.lat !== 0),
  );
  // TODO(shipping-revisit): selectors for the hidden Shipping row are
  // commented out below so they don't trigger unused-variable warnings
  // while the picker UI is on hold. Re-add when the shipping summary
  // is brought back.
  // const allocationStrategy = usePublishOrderStore(s => s.allocationStrategy);
  // const targetOrgId = usePublishOrderStore(s => s.targetOrgId);
  // const targetLabel = usePublishOrderStore(s => s.targetLabel);
  const load = usePublishOrderStore(s => s.load);
  const pickupDate = usePublishOrderStore(s => s.pickupDate);
  const totalVehicles = usePublishOrderStore(selectTotalVehicles);
  const approximateStops = usePublishOrderStore(useShallow(selectApproximateStops));
  const isReady = usePublishOrderStore(selectIsReadyToPublish);
  const sessionOrders = usePublishOrderStore(useShallow(s => s.sessionOrders));
  const sessionStats = usePublishOrderStore(useShallow(selectSessionStats));
  const clearSession = usePublishOrderStore(s => s.clearSession);

  // Reuses `getOrderDisplayFromTo` so the format matches the rest of the app
  // (customer dashboard, order detail, etc.): "Today · 5:00 PM – 7:00 PM",
  // "Tomorrow · 9:00 AM – 11:00 AM", or "May 24 · 9:00 AM – 11:00 AM".
  const pickupDisplay = pickupDate
    ? getOrderDisplayFromTo({
        from: pickupDate.from,
        to: pickupDate.to,
        translate: (key, fallback) => {
          const map: Record<string, string> = {
            'date.today': 'c-dates:relative_dates.today',
            'date.tomorrow': 'c-dates:relative_dates.tomorrow',
          };
          return t(map[key] ?? key, { defaultValue: fallback ?? key });
        },
        isRTL: i18n.dir() === 'rtl',
      })
    : null;
  const isPickupNow = pickupDate ? isNowSlot(pickupDate.from) : false;

  return (
    <div className="flex flex-col gap-4">
      <OrderMapPreview />

      <Card className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">
          {t('wizard.side.summary_title', { defaultValue: 'Order summary' })}
        </div>

        <div className="space-y-3 text-sm">
          <SummaryRow
            icon={<UserIcon className="size-4" />}
            label={t('wizard.customer.title', { defaultValue: 'Client' })}
            primary={
              isInternalShipment
                ? t('wizard.customer.internal_title', { defaultValue: 'Internal shipment' })
                : customer?.name
            }
            secondary={!isInternalShipment ? customer?.phone : undefined}
            empty={t('wizard.side.customer_empty', { defaultValue: 'Pick a client' })}
          />

          {/* TODO(shipping-revisit): Shipping summary row is hidden
              while the CarrierSection picker is on hold. The store
              still defaults `allocationStrategy` to `'auto'` so the
              publish payload stays valid. Re-enable when the picker
              comes back. */}
          {/*
          <SummaryRow
            icon={<TruckIcon className="size-4 text-gray-700" />}
            label={t('wizard.shipping.title', { defaultValue: 'Shipping' })}
            primary={
              // Phase 5 — when the user picked the synthetic
              // "specific carrier" tile (`private_network` +
              // `targetOrgId`), surface the target's name as the
              // headline so the side panel matches what was
              // chosen on the left, not the raw strategy.
              allocationStrategy === 'private_network' && targetOrgId && targetLabel
                ? targetLabel
                : allocationStrategy
                ? t(`allocation.strategy.${allocationStrategy}.title`)
                : undefined
            }
            secondary={
              allocationStrategy === 'private_network' && targetOrgId
                ? t('allocation.strategy.specific_carrier.title', {
                    defaultValue: 'Specific carrier',
                  })
                : undefined
            }
            empty={t('wizard.side.shipping_empty', { defaultValue: 'Pick a shipping option' })}
          />
          */}

          <SummaryRow
            icon={<TruckIcon className="size-4 text-gray-700" />}
            label={t('form.load_details.title')}
            primary={
              load.vehicleType
                ? t(`m-vehicle:types.${load.vehicleType}`, {
                    defaultValue: String(load.vehicleType),
                  })
                : undefined
            }
            secondary={
              [
                load.itemType,
                load.quantity ? `${load.quantity}×` : undefined,
                load.weightTon ? `${load.weightTon} ton` : undefined,
              ]
                .filter(Boolean)
                .join(' · ') || undefined
            }
            empty={t('wizard.side.load_empty', { defaultValue: 'Pick vehicle & load' })}
            onClick={onEditLoad}
          />

          <SummaryRow
            icon={<DateIcon className="size-4" />}
            label={t('pickup_date.title')}
            primary={
              pickupDisplay
                ? isPickupNow
                  ? // "Now" already implies today, so the relative "Today" label
                    // would be redundant: just show the time range.
                    pickupDisplay.timeOnly
                  : `${pickupDisplay.dateAutoOnly} · ${pickupDisplay.timeOnly}`
                : undefined
            }
            empty={t('wizard.side.date_empty', { defaultValue: 'Pick a pickup date' })}
            onClick={onEditPickup}
            badge={
              isPickupNow ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                  <ZapIcon className="size-3" />
                  {t('c-dates:now', { defaultValue: 'Now' })}
                </span>
              ) : undefined
            }
          />
        </div>

        {/* Footer block — Total is the headline (large, on top), with
            "Total trucks" sitting underneath as a small supporting
            line. Drops entirely until the user has picked at least
            one destination, so a fresh draft doesn't show a stray
            divider with no content. */}
        {hasAnyDestination && (
          <div className="mt-4 pt-3 border-t space-y-2">
            <PriceSummaryCard />
            {totalVehicles > 0 && (
              <div className="flex items-center justify-between text-[12px] text-gray-500">
                <span>
                  {t('wizard.side.total_trucks', { defaultValue: 'Total trucks' })}
                </span>
                <span className="font-medium text-gray-700">{totalVehicles}</span>
              </div>
            )}
          </div>
        )}

        {approximateStops.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 flex items-start gap-2">
            <AlertCircleIcon className="size-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0 text-[11px] text-amber-800 leading-snug">
              {approximateStops.length === 1
                ? t('wizard.side.approximate_warning_one', {
                    count: approximateStops.length,
                    defaultValue:
                      '{{count}} stop is approximate — dispatch will chase the precise pin.',
                  })
                : t('wizard.side.approximate_warning_other', {
                    count: approximateStops.length,
                    defaultValue:
                      '{{count}} stops are approximate — dispatch will chase the precise pins.',
                  })}
            </div>
          </div>
        )}
      </Card>

      {sessionOrders.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('wizard.session.title', { defaultValue: 'This session' })}
              <span className="ms-2 text-gray-400 normal-case tracking-normal">
                {t('wizard.session.summary', {
                  defaultValue: '{{orders}} orders · {{trucks}} trucks',
                  orders: sessionStats.orders,
                  trucks: sessionStats.trucks,
                })}
              </span>
            </div>
            <button
              type="button"
              onClick={clearSession}
              className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700">
              <TrashIcon className="size-3.5" />
              {t('wizard.session.clear', { defaultValue: 'Clear' })}
            </button>
          </div>

          <ul className="space-y-2">
            {sessionOrders.map(entry => (
              <SessionRow
                key={entry.id}
                entry={entry}
                internalLabel={t('wizard.session.internal_label', {
                  defaultValue: 'Internal shipment',
                })}
                trucksLabel={t('wizard.vehicle_count', { defaultValue: 'Trucks' })}
              />
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-col gap-2">
        <Button
          size="lg"
          onClick={onPublish}
          disabled={!isReady || isPublishing}
          className="w-full">
          {isPublishing
            ? t('wizard.side.publishing', { defaultValue: 'Publishing…' })
            : sessionOrders.length > 0
              ? t('wizard.side.publish_another_cta', { defaultValue: 'Publish another order' })
              : t('wizard.side.publish_cta', { defaultValue: 'Publish order' })}
        </Button>
        {onDone && (
          <button
            type="button"
            onClick={onDone}
            className="self-center text-xs text-gray-500 hover:text-gray-700">
            {sessionOrders.length > 0
              ? t('wizard.side.done_with_count', {
                  defaultValue: "I'm done ({{count}} created)",
                  count: sessionStats.orders,
                })
              : t('wizard.side.done', { defaultValue: 'Back to dashboard' })}
          </button>
        )}
      </div>
    </div>
  );
}

interface SessionRowProps {
  entry: PublishSessionEntry;
  internalLabel: string;
  trucksLabel: string;
}

function SessionRow({ entry, internalLabel, trucksLabel }: SessionRowProps) {
  const time = entry.pickupFromIso
    ? new Date(entry.pickupFromIso).toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : undefined;

  const secondary = [
    `${entry.vehicleCount} ${trucksLabel.toLowerCase()}`,
    entry.routeCount > 1
      ? `${entry.routeCount} ${entry.routeCount === 1 ? 'route' : 'routes'}`
      : undefined,
    time,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="flex items-start gap-2.5 rounded-md border border-emerald-100 bg-emerald-50/40 px-2.5 py-2">
      <div className="mt-0.5 flex size-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
        <CheckIcon className="size-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900 truncate">
          {entry.isInternalShipment ? internalLabel : entry.receiverName ?? internalLabel}
        </div>
        <div className="text-[11px] text-gray-500 truncate">{secondary}</div>
      </div>
    </li>
  );
}

interface SummaryRowProps {
  icon: React.ReactNode;
  label: string;
  primary?: string;
  secondary?: string;
  empty?: string;
  /**
   * Optional pill rendered alongside the primary text (e.g. the "Now"
   * indicator on the pickup row when the slot is today's immediate one).
   */
  badge?: React.ReactNode;
  /**
   * When provided, the row renders as a button (with hover state + a small
   * pencil hint). Used by Load and Pickup rows so the side panel acts as
   * the edit surface for those fields and the carrier doesn't need a
   * separate left-side trigger card.
   */
  onClick?: () => void;
}

function SummaryRow({
  icon,
  label,
  primary,
  secondary,
  empty,
  badge,
  onClick,
}: SummaryRowProps) {
  const isEmpty = !primary;
  const content = (
    <>
      <div className="mt-0.5 flex size-7 items-center justify-center rounded-full bg-gray-100 text-gray-500 flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
        {isEmpty ? (
          <div className="text-sm italic text-gray-400">{empty ?? '—'}</div>
        ) : (
          <>
            <div className="flex items-center gap-1.5 min-w-0">
              {badge}
              <span className="text-sm font-semibold text-gray-900 truncate">{primary}</span>
            </div>
            {secondary && <div className="text-xs text-gray-500 truncate">{secondary}</div>}
          </>
        )}
      </div>
      {onClick && (
        <PencilIcon className="mt-1 size-3.5 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors" />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'group flex w-full items-start gap-3 rounded-md text-start',
          '-mx-1 px-1 py-1 transition-colors',
          'hover:bg-gray-50',
        )}>
        {content}
      </button>
    );
  }

  return <div className="flex items-start gap-3">{content}</div>;
}
